
import React from 'react';
import { Button } from '@/components/ui/button';
import { Crown, ArrowRight } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { getRewardfulReferral } from '@/utils/rewardful';
import { redirectToCheckout } from '@/lib/analytics';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

interface PremiumUpsellProps {
  onPurchase: () => void;
}

const features = [
  'Moon Planner and Trip Planner',
  'PDF, Excel, CSV, and calendar exports',
];

export const PremiumUpsell: React.FC<PremiumUpsellProps> = ({ onPurchase }) => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const handleUpgrade = async () => {
    // If not authenticated, redirect to register page
    if (!user) {
      navigate('/register');
      return;
    }

    try {
      const { data, error } = await supabase.functions.invoke('create-checkout', {
        body: {
          returnUrl: `${window.location.origin}/dashboard?welcome=true`,
          referralId: getRewardfulReferral()
        }
      });

      if (error) throw error;
      if (data?.url) {
        redirectToCheckout(data.url, 'Premium');
      }
    } catch (error) {
      console.error('Error creating checkout:', error);
      toast.error('Unable to start checkout. Please try again.');
    }
  };

  return (
    <div className="max-w-xl mx-auto">
      <div className="bg-white rounded-[4px] border border-border overflow-hidden">
        {/* Gold gradient line at top */}
        <div className="h-1 bg-gradient-to-r from-gold to-gold-light" />

        <div className="p-8 sm:p-10">
          <div className="flex items-start gap-3 mb-2">
            <Crown className="w-5 h-5 text-gold mt-0.5 shrink-0" />
            <h2 className="text-xl text-foreground tracking-[-0.02em]">
              Get more with Premium
            </h2>
          </div>
          <p className="text-sm text-muted-foreground mb-8 ml-8">
            Everything above stays free. Premium adds:
          </p>

          <ul className="space-y-3 mb-10">
            {features.map((feature) => (
              <li key={feature} className="flex items-start gap-3 text-sm text-foreground">
                <span className="text-gold mt-0.5 w-5 text-center shrink-0">✦</span>
                {feature}
              </li>
            ))}
          </ul>

          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pt-8 border-t border-border">
            <div>
              <span className="text-3xl font-bold text-foreground tracking-tight font-serif">$25</span>
              <span className="text-muted-foreground ml-1">/year</span>
              <span className="text-xs text-muted-foreground ml-2">($2.08/mo)</span>
            </div>
            <Button
              variant="premium"
              onClick={handleUpgrade}
              size="lg"
              className="px-8 transition-all"
            >
              <Crown className="w-4 h-4 mr-2" />
              Get Premium
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
